// notifyStream.js — live notifications over Server-Sent Events.
//
// Replaces the SignalR client. Not because SignalR was wrong, but because its
// WebSocket could not survive the path this API sits behind: Cloudflare in
// front of IIS never completed the upgrade, so every connection came back 404,
// while the identical code connected first time against Kestrel on localhost.
// SSE is plain HTTP - there is no handshake to strip.
//
// Read with fetch() rather than the browser's EventSource, for one reason that
// matters: EventSource cannot set request headers, so the token would have to
// travel as ?access_token= - in the URL, and therefore in every proxy log,
// server log and browser history entry. That is exactly what the WebSocket
// version was forced to do. fetch sends a normal Authorization header.
//
// What arrives is a SIGNAL, never the data: "a QR order arrived", not the
// order. The caller then asks for the list through the normal API. A pushed
// payload that is lost is lost for good; a lost signal costs one refresh.

import config from '../../config';

/**
 * Opens the stream and calls back as events arrive.
 *
 * @param {object}   opts
 * @param {function} opts.onEvent      (eventName, data) for every event
 * @param {function} opts.onOpen       stream is live
 * @param {function} opts.onClose      stream ended or failed
 * @returns {function} call it to close the stream
 */
export function openNotifyStream({ onEvent, onOpen, onClose } = {}) {
  const controller = new AbortController();
  let stopped = false;
  // Backs off on repeated failure so a server that is down is not hammered by
  // every open tab. Reset the moment a connection succeeds.
  let retryMs = 2000;
  const RETRY_MAX = 30000;

  const base = config.API_BASE_URL.replace(/\/$/, '');

  const run = async () => {
    while (!stopped) {
      try {
        const res = await fetch(`${base}/notify/stream`, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`,
            Accept: 'text/event-stream',
          },
          signal: controller.signal,
          // Never from cache. A cached stream is a stream that never arrives.
          cache: 'no-store',
        });

        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);

        retryMs = 2000;
        onOpen?.();

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // A frame ends at a BLANK LINE. Chunks arrive at whatever boundary
          // the network felt like, so a frame can be split across two reads -
          // whatever follows the last blank line stays in the buffer for the
          // next one. Splitting on every newline instead is how half an event
          // gets parsed as a whole one.
          let cut = buffer.indexOf('\n\n');
          while (cut !== -1) {
            const frame = buffer.slice(0, cut);
            buffer = buffer.slice(cut + 2);
            handleFrame(frame, onEvent);
            cut = buffer.indexOf('\n\n');
          }
        }
      } catch (err) {
        // AbortError is us closing it - a tab change, not a failure.
        if (controller.signal.aborted) break;
      }

      onClose?.();
      if (stopped) break;

      await new Promise((r) => setTimeout(r, retryMs));
      retryMs = Math.min(retryMs * 2, RETRY_MAX);
    }
  };

  run();

  return () => {
    stopped = true;
    try {
      controller.abort();
    } catch {
      /* already gone */
    }
  };
}

function handleFrame(frame, onEvent) {
  // ": ping" - the server's heartbeat, which exists only to stop Cloudflare
  // closing an idle connection at ~100s. Nothing to report.
  if (!frame || frame.startsWith(':')) return;

  let eventName = 'message';
  const dataLines = [];

  frame.split('\n').forEach((line) => {
    if (line.startsWith('event:')) eventName = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  });

  if (dataLines.length === 0) return;

  let data = null;
  try {
    data = JSON.parse(dataLines.join('\n'));
  } catch {
    // A frame we cannot read is not worth taking the stream down for.
    return;
  }

  onEvent?.(eventName, data);
}
